// AGC: useFormTelemetry — wraps a form submit promise, emits success/fail with mapped error codes.
// AGC: Sets flow context on mount, clears on successful submit.
import { ZodError } from 'zod'
import { useEffect, useRef } from 'react'
import { track, setFlowContext, clearFlowContext } from '../lib/analytics'
import type { FormName, FormErrorCode } from '../lib/analytics-events'

function classifyError(err: unknown): FormErrorCode {
  if (err instanceof ZodError) return 'validation'
  if (err instanceof TypeError && /fetch/i.test(err.message)) return 'network'
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: unknown }).code
    if (code === 'permission-denied') return 'permission'
    if (code === 'already-exists') return 'duplicate'
  }
  return 'unknown'
}

export function useFormTelemetry(formName: FormName, entryPoint?: string) {
  const flowIdRef = useRef<string | null>(null)

  useEffect(() => {
    flowIdRef.current = setFlowContext(entryPoint ?? formName)
    return () => { clearFlowContext() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trackSubmit = async <T>(submit: () => Promise<T>, fieldName?: string): Promise<T> => {
    const startedAt = Date.now()
    try {
      const result = await submit()
      await track('form_submit_success', {
        form_name: formName,
        duration_ms: Date.now() - startedAt,
      })
      clearFlowContext()
      return result
    } catch (err) {
      await track('form_submit_fail', {
        form_name: formName,
        error_code: classifyError(err),
        ...(fieldName ? { field_name: fieldName } : {}),
      })
      throw err
    }
  }

  return { trackSubmit }
}
